import { z } from 'zod';
import type { HealthState } from './resources.js';

/**
 * VPN connections between resources, for the network map.
 *
 * Three sources feed the same picture:
 *  - manual: an operator lists a resource's tunnels on the resource itself
 *    (`vpnLinks`), naming the far-end resource and both tunnel addresses;
 *  - ppp: an SSH read of a VPN concentrator's `/ppp active` table (plus its
 *    addresses and routes) — one link per connected client, matched to a
 *    monitored resource by tunnel address or by a subnet routed into the
 *    tunnel;
 *  - p2p: two resources whose interface addresses name each other as the far
 *    end of a point-to-point link (RouterOS `network=` on a /32), or share a
 *    tunnel subnet.
 *
 * Resolution is done at read time, not stored, so a resource added or
 * re-addressed after the last discovery still lines up with it.
 */

/* ------------------------------------------------------------------ */
/* IPv4 address + mask                                                 */
/* ------------------------------------------------------------------ */

export interface Ipv4WithMask {
  ip: string;
  /** Prefix length; null when only a bare address is known. */
  prefix: number | null;
}

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

export function isIpv4(value: string): boolean {
  const m = IPV4_RE.exec(value.trim());
  return Boolean(m && m.slice(1).every((o) => Number(o) <= 255));
}

export function ipv4ToInt(ip: string): number {
  return ip.split('.').reduce((n, o) => n * 256 + Number(o), 0);
}

function intToIpv4(n: number): string {
  return [24, 16, 8, 0].map((s) => Math.floor(n / 2 ** s) % 256).join('.');
}

export function prefixToNetmask(prefix: number): string {
  return intToIpv4(prefix === 0 ? 0 : (0xffffffff - (2 ** (32 - prefix) - 1)) >>> 0);
}

/** Dotted netmask to prefix length; null for a non-contiguous mask. */
export function netmaskToPrefix(mask: string): number | null {
  if (!isIpv4(mask)) return null;
  const bits = ipv4ToInt(mask).toString(2).padStart(32, '0');
  if (!/^1*0*$/.test(bits)) return null;
  const firstZero = bits.indexOf('0');
  return firstZero === -1 ? 32 : firstZero;
}

/**
 * Accepts `10.0.0.1`, `10.0.0.1/24` or `10.0.0.1/255.255.255.0` (what
 * operators paste from Winbox, `ip a` and Windows respectively). Returns null
 * for anything else.
 */
export function parseIpv4WithMask(value: string | null | undefined): Ipv4WithMask | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;
  const [ip, mask, ...rest] = raw.split('/');
  if (rest.length > 0 || !ip || !isIpv4(ip)) return null;
  if (mask === undefined) return { ip, prefix: null };
  if (/^\d{1,2}$/.test(mask)) {
    const prefix = Number(mask);
    return prefix <= 32 ? { ip, prefix } : null;
  }
  const prefix = netmaskToPrefix(mask);
  return prefix === null ? null : { ip, prefix };
}

export function formatIpv4WithMask(value: Ipv4WithMask): string {
  return value.prefix === null ? value.ip : `${value.ip}/${value.prefix}`;
}

/** `true` when `ip` falls inside `network/prefix`. */
export function ipv4InSubnet(ip: string, network: string, prefix: number): boolean {
  if (!isIpv4(ip) || !isIpv4(network)) return false;
  const size = 2 ** (32 - prefix);
  return Math.floor(ipv4ToInt(ip) / size) === Math.floor(ipv4ToInt(network) / size);
}

/* ------------------------------------------------------------------ */
/* Stored shapes                                                       */
/* ------------------------------------------------------------------ */

const optionalIpWithMask = z
  .string()
  .trim()
  .max(40)
  .default('')
  .refine((v) => v === '' || parseIpv4WithMask(v) !== null, 'Use an IPv4 address, optionally with /prefix or /netmask');

/** One tunnel an operator recorded on a resource by hand. */
export const vpnLinkSchema = z.object({
  /** The far-end resource; null when it isn't monitored here (a provider hub). */
  peerId: z.string().nullable().default(null),
  /** "SSTP to HQ", "WireGuard wg0" — also names an unmonitored peer. */
  label: z.string().trim().max(60).default(''),
  /** This resource's tunnel address, e.g. `19.18.17.245/24`. */
  localAddress: optionalIpWithMask,
  /** The far end's tunnel address. */
  remoteAddress: optionalIpWithMask,
});
export type VpnLink = z.infer<typeof vpnLinkSchema>;

/** An address configured on one of a device's interfaces. */
export interface InterfaceAddress {
  interface: string;
  ip: string;
  prefix: number;
  /** RouterOS `network=` — on a point-to-point /32, the far end's address. */
  network?: string | null;
  /** The interface is a tunnel (PPP, SSTP, L2TP, OpenVPN, WireGuard, GRE...). */
  tunnel: boolean;
}

/** One row of a concentrator's `/ppp active`, joined with its address and routes. */
export interface PppSession {
  /** PPP secret / user name. */
  name: string;
  /** sstp, l2tp, pptp, ovpn, pppoe. */
  service: string;
  /** The client's public address as the server saw it. */
  callerId: string | null;
  /** The client's tunnel address. */
  address: string | null;
  uptime: string | null;
  /** The server-side dynamic interface, e.g. `<sstp-prq>`. */
  interface: string | null;
  /** The server's own address on that interface. */
  localAddress: Ipv4WithMask | null;
  /** Subnets the server routes into this tunnel — the sites behind it. */
  routes: string[];
}

/** What an SSH read of a RouterOS device found; kept in `attributes.vpnDiscovery`. */
export interface VpnDiscovery {
  ts: number;
  addresses: InterfaceAddress[];
  sessions: PppSession[];
}

/** Interface names/types that are tunnels, across RouterOS, Linux and SNMP ifDescr. */
export function isTunnelInterface(name: string, type?: string | null): boolean {
  // A known type is authoritative — a bridge named "bridge-vpn" is still a bridge.
  if (type) return /-(in|out)$|^(wg|wireguard|gre|eoip|ipip|ipsec|vxlan|ovpn|sstp|l2tp|pptp|pppoe|ppp)/i.test(type);
  return /^<.*>$|sstp|l2tp|pptp|ovpn|openvpn|wireguard|^wg\d*|^tun\d*|^tap\d*|^gre|eoip|ipip|ipsec|vpn|^ppp/i.test(name);
}

/* ------------------------------------------------------------------ */
/* Resolution                                                          */
/* ------------------------------------------------------------------ */

export type VpnLinkSource = 'manual' | 'ppp' | 'p2p';

export interface VpnEndpoint {
  /** null when the far end isn't a monitored resource. */
  resourceId: string | null;
  name: string;
  address: Ipv4WithMask | null;
}

export interface ResolvedVpnLink {
  id: string;
  source: VpnLinkSource;
  label: string;
  a: VpnEndpoint;
  b: VpnEndpoint;
  /** PPP extras, when the link came from a concentrator. */
  service?: string;
  callerId?: string | null;
  uptime?: string | null;
  routes?: string[];
}

/** The subset of a resource that link resolution reads. */
export interface VpnResourceLike {
  id: string;
  name: string;
  kind: string;
  parentId: string | null;
  address: string | null;
  secondaryAddresses?: Array<{ value: string }>;
  config?: Record<string, unknown>;
  attributes?: Record<string, unknown>;
  vpnLinks?: VpnLink[];
}

const GATEWAY_KINDS = /router|load-balancer|firewall|gateway|mikrotik|fortigate|pfsense|opnsense|sonicwall|vpn/i;

export function vpnDiscoveryOf(resource: VpnResourceLike): VpnDiscovery | null {
  const d = resource.attributes?.vpnDiscovery as VpnDiscovery | undefined;
  return d && Array.isArray(d.addresses) && Array.isArray(d.sessions) ? d : null;
}

/**
 * Every interface address known for a resource: the SSH read when there is
 * one (it has the point-to-point far ends), otherwise the SNMP IP-MIB walk.
 */
export function interfaceAddressesOf(resource: VpnResourceLike): InterfaceAddress[] {
  const ssh = vpnDiscoveryOf(resource)?.addresses;
  if (ssh?.length) return ssh;
  const snmp = resource.attributes?.interfaceAddresses;
  return Array.isArray(snmp) ? (snmp as InterfaceAddress[]) : [];
}

/** Every IPv4 a resource is known to answer on. */
function ipsOf(resource: VpnResourceLike): string[] {
  const raw = [
    String(resource.config?.host ?? ''),
    resource.address ?? '',
    ...(resource.secondaryAddresses ?? []).map((a) => a.value),
    ...interfaceAddressesOf(resource).map((a) => a.ip),
  ];
  return [...new Set(raw.map((v) => v.trim().split('/')[0] ?? '').filter(isIpv4))];
}

/**
 * Which monitored resource sits at the far end of a PPP session: one that
 * owns the session's tunnel address outright, else a gateway-looking
 * resource inside a subnet routed into the tunnel. A routed /24 holds a
 * whole site, so the router beats the printers, and a top-level node beats
 * one nested under it.
 */
function matchSessionPeer(session: PppSession, candidates: VpnResourceLike[]): VpnResourceLike | null {
  if (session.address) {
    const exact = candidates.find((r) => ipsOf(r).includes(session.address!));
    if (exact) return exact;
  }
  const subnets = session.routes
    .map((r) => parseIpv4WithMask(r))
    .filter((s): s is Ipv4WithMask & { prefix: number } => s !== null && s.prefix !== null && s.prefix >= 8);
  if (subnets.length === 0) return null;

  let best: { resource: VpnResourceLike; score: number } | null = null;
  for (const r of candidates) {
    const ips = ipsOf(r).filter((ip) => subnets.some((s) => ipv4InSubnet(ip, s.ip, s.prefix)));
    if (ips.length === 0) continue;
    const score =
      (GATEWAY_KINDS.test(r.kind) ? 4 : 0) + (ips.some((ip) => ip.endsWith('.1')) ? 2 : 0) + (r.parentId ? 0 : 1);
    if (!best || score > best.score) best = { resource: r, score };
  }
  return best?.resource ?? null;
}

const pairKey = (x: string | null, y: string | null) => [x ?? '', y ?? ''].sort().join('|');

/**
 * Every VPN link among `resources`. Manual entries win over discovered ones
 * for the same pair of resources (the operator knows better than a guess),
 * and a PPP session wins over the bare address pairing of the same tunnel.
 */
export function resolveVpnLinks(resources: VpnResourceLike[]): ResolvedVpnLink[] {
  const byId = new Map(resources.map((r) => [r.id, r]));
  const links: ResolvedVpnLink[] = [];
  const pairs = new Set<string>();
  const endpoint = (r: VpnResourceLike, address: Ipv4WithMask | null): VpnEndpoint => ({ resourceId: r.id, name: r.name, address });

  // A link counted from both of its ends is still one link.
  const add = (link: ResolvedVpnLink, key: string, dedupeByPair: boolean) => {
    if (dedupeByPair && link.b.resourceId && pairs.has(key)) return;
    if (links.some((l) => l.id === link.id)) return;
    links.push(link);
    if (link.b.resourceId) pairs.add(key);
  };

  /* manual */
  for (const r of resources) {
    (r.vpnLinks ?? []).forEach((l, i) => {
      const peer = l.peerId ? byId.get(l.peerId) : undefined;
      const key = pairKey(r.id, peer?.id ?? null);
      // Recorded on both ends: keep the first, but let it borrow the other
      // side's addresses if it left them blank.
      const twin = links.find((x) => x.source === 'manual' && peer && pairKey(x.a.resourceId, x.b.resourceId) === key);
      if (twin) {
        if (!twin.b.address && l.localAddress) twin.b.address = parseIpv4WithMask(l.localAddress);
        if (!twin.a.address && l.remoteAddress) twin.a.address = parseIpv4WithMask(l.remoteAddress);
        return;
      }
      add(
        {
          id: `manual:${r.id}:${i}`,
          source: 'manual',
          label: l.label,
          a: endpoint(r, parseIpv4WithMask(l.localAddress)),
          b: peer
            ? endpoint(peer, parseIpv4WithMask(l.remoteAddress))
            : { resourceId: null, name: l.label || 'External peer', address: parseIpv4WithMask(l.remoteAddress) },
        },
        key,
        false,
      );
    });
  }

  /* ppp — a concentrator's connected clients */
  for (const hub of resources) {
    const sessions = vpnDiscoveryOf(hub)?.sessions ?? [];
    if (sessions.length === 0) continue;
    const others = resources.filter((r) => r.id !== hub.id);
    for (const s of sessions) {
      const peer = matchSessionPeer(s, others);
      const remote = s.address ? { ip: s.address, prefix: s.localAddress?.prefix ?? null } : null;
      add(
        {
          id: `ppp:${hub.id}:${s.service}:${s.name}`,
          source: 'ppp',
          label: `${s.service.toUpperCase()} ${s.name}`,
          a: endpoint(hub, s.localAddress),
          b: peer ? endpoint(peer, remote) : { resourceId: null, name: s.name, address: remote },
          service: s.service,
          callerId: s.callerId,
          uptime: s.uptime,
          routes: s.routes,
        },
        pairKey(hub.id, peer?.id ?? null),
        true,
      );
    }
  }

  /* p2p — two ends that name each other, or share a tunnel subnet */
  const tunnelAddrs = resources.flatMap((r) =>
    interfaceAddressesOf(r)
      .filter((a) => a.tunnel)
      .map((a) => ({ r, a })),
  );
  for (let i = 0; i < tunnelAddrs.length; i++) {
    for (let j = i + 1; j < tunnelAddrs.length; j++) {
      const x = tunnelAddrs[i]!;
      const y = tunnelAddrs[j]!;
      if (x.r.id === y.r.id || x.a.ip === y.a.ip) continue;
      const pointToPoint = x.a.network === y.a.ip || y.a.network === x.a.ip;
      const sharedSubnet =
        x.a.prefix === y.a.prefix && x.a.prefix >= 16 && x.a.prefix <= 30 && ipv4InSubnet(x.a.ip, y.a.ip, x.a.prefix);
      if (!pointToPoint && !sharedSubnet) continue;
      add(
        {
          id: `p2p:${[`${x.r.id}@${x.a.ip}`, `${y.r.id}@${y.a.ip}`].sort().join(':')}`,
          source: 'p2p',
          label: x.a.interface.replace(/^<|>$/g, ''),
          a: endpoint(x.r, { ip: x.a.ip, prefix: x.a.prefix }),
          b: endpoint(y.r, { ip: y.a.ip, prefix: y.a.prefix }),
        },
        pairKey(x.r.id, y.r.id),
        true,
      );
    }
  }

  return links;
}

/** A link as the API hands it to the browser: endpoints carry what the map needs. */
export interface VpnLinkView extends ResolvedVpnLink {
  a: VpnEndpoint & { health?: HealthState; customerId?: string | null };
  b: VpnEndpoint & { health?: HealthState; customerId?: string | null };
  /** The tunnel itself, as the periodic VPN watch last found it; absent until it has checked this link. */
  status?: VpnLinkStatus;
}

export interface VpnLinkStatus {
  /** `unknown` when no router on either end could be asked. */
  state: 'up' | 'down' | 'unknown';
  checkedAt: number;
  /** Since when it has read `down` — set on the first failed check. */
  downSince: number | null;
  /** "ping 3/3 from X", "PPP session not connected on HUB" … */
  detail: string;
  /** The MikroTik that ran the check. */
  checkedFrom: string | null;
}
